import React, {
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { RefreshCcw } from "lucide-react";
import axiosClient from "../api/axiosClient";
import { formatTest, sortNewestFirst } from "../script/formatTestName";
import "../styles/AdminDashboardTable.css";
import "../styles/Logindropdown.css";

// Default course list to fall back on when a user has no courses assigned yet
const DEFAULT_COURSES = ["Python", "DSA", "Data Science", "C", "Java"];
const PLAN_TYPES = ["FREE", "STANDARD", "PREMIUM", "BASIC"];

const EMPTY_STUDENT_FORM = {
  fullName: "",
  lastName: "",
  email: "",
  mobileNo: "",
  collegeName: "",
  collegeID: "", // FIX: was "collegeId" in state but "collegeID" in the input/payload
  isStrictlyRestricted: false,
  allowedIps: [""],
};

const getAuthHeaders = () => ({
  Authorization: `${localStorage.getItem("admintoken")}`,
});

/* ------------------------------------------------------------------ */
/* Friendly test name label (raw name stays in the tooltip)            */
/* ------------------------------------------------------------------ */
const TestLabel = ({ name }) => {
  const { title, subtitle, badge } = formatTest(name);
  return (
    <span className="test-label" title={name}>
      <span className="test-label-top">
        <span className="test-label-title">{title}</span>
        {badge && (
          <span className={`test-badge test-badge-${badge.toLowerCase()}`}>
            {badge}
          </span>
        )}
      </span>
      {subtitle && <span className="test-label-sub">{subtitle}</span>}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Last Login filter dropdown                                          */
/* ------------------------------------------------------------------ */
const LoginDropdown = ({ onApply }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("");
  const [customDate, setCustomDate] = useState({ from: "", to: "" });
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const boxRef = useRef(null);

  const handleApply = () => {
    let payload = {};
    if (selected === "today") payload = { loginTimePeriod: "TODAY" };
    if (selected === "weekly") payload = { loginTimePeriod: "WEEKLY" };
    if (selected === "monthly") payload = { loginTimePeriod: "MONTHLY" };
    if (selected === "custom") {
      payload = {
        loginTimePeriod: "CUSTOM",
        loginTimeStartDate: customDate.from,
        loginTimeEndDate: customDate.to,
      };
    }
    onApply(payload);
    setOpen(false);
  };

  const toggleOpen = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 8, left: rect.left });
    }
    setOpen((prev) => !prev);
  };

  useLayoutEffect(() => {
    if (!open) return;

    const reposition = () => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 8, left: rect.left });
    };

    const handleOutsideClick = (e) => {
      const clickedButton = btnRef.current?.contains(e.target);
      const clickedBox = boxRef.current?.contains(e.target);
      if (!clickedButton && !clickedBox) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open]);

  const options = [
    { value: "today", label: "Today" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "custom", label: "Custom Date" },
  ];

  const customIncomplete =
    selected === "custom" && (!customDate.from || !customDate.to);

  return (
    <div className="filter-wrapper">
      <button
        type="button"
        ref={btnRef}
        className="filter-open-btn"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={toggleOpen}
      >
        Last Login <span className="filter-caret">▾</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={boxRef}
            onMouseDown={(e) => e.stopPropagation()}
            className="filter-dropdown-box"
            style={{ top: coords.top, left: coords.left }}
          >
            <p className="filter-title">Filter Login</p>
            <div className="line"></div>

            {options.map((opt) => (
              <label className="filter-option" key={opt.value}>
                <input
                  type="checkbox"
                  checked={selected === opt.value}
                  onChange={() => setSelected(opt.value)}
                />
                {opt.label}
              </label>
            ))}

            {selected === "custom" && (
              <div className="custom-date-box">
                <input
                  type="date"
                  value={customDate.from}
                  onChange={(e) =>
                    setCustomDate((prev) => ({ ...prev, from: e.target.value }))
                  }
                />
                <input
                  type="date"
                  value={customDate.to}
                  onChange={(e) =>
                    setCustomDate((prev) => ({ ...prev, to: e.target.value }))
                  }
                />
              </div>
            )}

            <button
              type="button"
              className="filterapply"
              onClick={handleApply}
              disabled={!selected || customIncomplete}
            >
              Apply Filter
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main table                                                          */
/* ------------------------------------------------------------------ */
export default function UserTable({
  pageSize,
  onUserClick,
  setSearch,
  setPageIndex,
  fetchStats,
  search,
  data,
  setLoginFilter,
  totalPages,
  pageIndex,
}) {
  const [sorting, setSorting] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  // ---- Reports popup state ----
  const [showPopup, setShowPopup] = useState(false);
  const [Reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [downloading, setDownloading] = useState(null);

  // ---- Add Student state ----
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [studentForm, setStudentForm] = useState(EMPTY_STUDENT_FORM);
  const [studentError, setStudentError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ---- Assign Subscription state ----
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const [subscriptionTarget, setSubscriptionTarget] = useState(null); // { userId, courses }
  const [subscriptionForm, setSubscriptionForm] = useState({
    courseName: "",
    planType: "FREE",
    expiryDate: "",
  });
  const [subscriptionError, setSubscriptionError] = useState("");
  const [subscriptionSubmitting, setSubscriptionSubmitting] = useState(false);

  // Runs a fetch while showing the table overlay
  const withTableLoading = async (fn) => {
    setTableLoading(true);
    try {
      await fn();
    } catch (err) {
      console.log(err);
    } finally {
      setTableLoading(false);
    }
  };

  /* ---------------- Reports ---------------- */
  const getreport = async (userId) => {
    // FIX: open popup and reset state up-front so stale data never shows
    setSelectedUser(userId);
    setReports([]);
    setReportsError("");
    setReportsLoading(true);
    setShowPopup(true);

    try {
      const res = await axiosClient.get(
        `/auth/admin/user-detail/userIdAndModuleName`,
        { params: { userId }, headers: getAuthHeaders() }
      );

      console.log("data", res.data);

      // FIX: tolerate missing / non-array listOfTestNames
      const names = Array.isArray(res.data?.testSummary?.listOfTestNames)
        ? res.data.testSummary.listOfTestNames
        : [];

      setReports(
        sortNewestFirst(
          names.filter(
            (test) =>
              typeof test === "string" &&
              !test.startsWith("CUSTOM_") &&
              !test.startsWith("DAILY")
          )
        )
      );
    } catch (error) {
      console.log(error);
      setReportsError(
        error?.response?.data?.message || "Failed to load reports"
      );
    } finally {
      setReportsLoading(false);
    }
  };

  const Downloadapi = async (item, userId) => {
    try {
      setDownloading(item);

      const res = await axiosClient.get(`/auth/admin/download-report`, {
        params: { userId, testName: item },
        headers: getAuthHeaders(),
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${item}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
      setReportsError("Failed to download report");
    } finally {
      setDownloading(null);
    }
  };

  /* ---------------- Search / reset / filter ---------------- */
  const handleSearch = () => {
    setPageIndex(0);
    return withTableLoading(() => fetchStats());
  };

  const handleReset = () => {
    setSearch("");
    setPageIndex(0);
    setLoginFilter({});
    return withTableLoading(() => fetchStats({}));
  };

  const handleApplyFilter = async (payload) => {
    setLoginFilter(payload);
    await withTableLoading(() => fetchStats(payload));
  };

  /* ---------------- Add Student ---------------- */
  const handleStudentChange = (e) => {
    const { name, value } = e.target;
    setStudentForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetStudentForm = () => {
    setStudentForm(EMPTY_STUDENT_FORM);
    setStudentError("");
  };

  const closeAddStudent = () => {
    setShowAddStudent(false);
    resetStudentForm();
  };

  const handleRestrictedChange = (value) => {
    setStudentForm((prev) => ({
      ...prev,
      isStrictlyRestricted: value,
      allowedIps: value ? prev.allowedIps : [""],
    }));
  };

  const handleIpChange = (index, value) => {
    setStudentForm((prev) => {
      const updated = [...prev.allowedIps];
      updated[index] = value;
      return { ...prev, allowedIps: updated };
    });
  };

  const addIpField = () => {
    setStudentForm((prev) => ({
      ...prev,
      allowedIps: [...prev.allowedIps, ""],
    }));
  };

  const removeIpField = (index) => {
    setStudentForm((prev) => ({
      ...prev,
      allowedIps: prev.allowedIps.filter((_, i) => i !== index),
    }));
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    setStudentError("");

    let cleanedIps = [];
    if (studentForm.isStrictlyRestricted) {
      cleanedIps = studentForm.allowedIps
        .map((ip) => ip.trim())
        .filter(Boolean);
      if (cleanedIps.length === 0) {
        setStudentError(
          "Please enter at least one IP address for restricted access"
        );
        return;
      }
    }

    try {
      setSubmitting(true);

      const payload = {
        fullName: studentForm.fullName,
        lastName: studentForm.lastName,
        email: studentForm.email,
        mobileNo: studentForm.mobileNo,
        collegeName: studentForm.collegeName,
        collegeId: studentForm.collegeID, // backend key kept as collegeId
        isStrictlyRestricted: studentForm.isStrictlyRestricted,
      };

      if (studentForm.isStrictlyRestricted) {
        payload.allowedIps = cleanedIps;
      }

      await axiosClient.post("/auth/admin/register", payload, {
        headers: getAuthHeaders(),
      });

      closeAddStudent();
      await withTableLoading(() => fetchStats());
    } catch (error) {
      console.log(error);
      setStudentError(
        error?.response?.data?.message || "Failed to add student"
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- Assign Subscription ---------------- */
  const openSubscriptionPopup = (row) => {
    const courseDetails = (row.courseDetails || []).filter((c) => c.courseName);

    // keep the plan along with the course name
    const courses =
      courseDetails.length > 0
        ? courseDetails.map((c) => ({
            courseName: c.courseName,
            planType: (c.planType || "FREE").toUpperCase(),
          }))
        : DEFAULT_COURSES.map((name) => ({ courseName: name, planType: null })); // not subscribed yet

    setSubscriptionTarget({ userId: row.userId, courses });
    setSubscriptionForm({
      courseName: courses[0].courseName,
      planType: courses[0].planType || "FREE",
      expiryDate: "",
    });
    setSubscriptionError("");
    setShowSubscriptionPopup(true);
  };

  const closeSubscriptionPopup = () => {
    setShowSubscriptionPopup(false);
    setSubscriptionTarget(null);
    setSubscriptionError("");
  };

  const handleSubscriptionChange = (e) => {
    const { name, value } = e.target;

    setSubscriptionForm((prev) => {
      if (name === "courseName") {
        const match = subscriptionTarget?.courses.find(
          (c) => c.courseName === value
        );
        return {
          ...prev,
          courseName: value,
          planType: match?.planType || "FREE",
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleGrantSubscription = async (e) => {
    e.preventDefault();
    if (!subscriptionTarget) return;

    if (!subscriptionForm.courseName) {
      setSubscriptionError("Please select a course");
      return;
    }

    setSubscriptionError("");

    try {
      setSubscriptionSubmitting(true);

      const payload = {
        userId: subscriptionTarget.userId,
        courseName: subscriptionForm.courseName,
        planType: subscriptionForm.planType,
        expiryDate: subscriptionForm.expiryDate || "",
      };

      await axiosClient.post("/auth/admin/grant-subscription", payload, {
        headers: getAuthHeaders(),
      });

      closeSubscriptionPopup();
      await withTableLoading(() => fetchStats());
    } catch (error) {
      console.log(error);
      setSubscriptionError(
        error?.response?.data?.message || "Failed to assign subscription"
      );
    } finally {
      setSubscriptionSubmitting(false);
    }
  };

  /* ---------------- Pagination window ---------------- */
  const visiblePages = 3;
  const start = Math.max(pageIndex - Math.floor(visiblePages / 2), 0);
  const end = Math.min(start + visiblePages, totalPages ?? 0);

  /* ---------------- Columns ----------------
     FIX: handlers live in a ref so `columns` stays stable. Previously the
     columns were rebuilt whenever fetchStats changed, which re-created the
     header component and remounted LoginDropdown (closing it and losing its
     selection). The ref also prevents stale closures in cell handlers. */
  const handlersRef = useRef({});
  handlersRef.current = {
    onUserClick,
    handleApplyFilter,
    openSubscriptionPopup,
    getreport,
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "userId",
        header: "User ID",
        cell: ({ row }) => {
          const userId = row.original.userId;

          return (
            <span
              title={userId}
              onClick={() => handlersRef.current.onUserClick(userId)}
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
                maxWidth: "120px",
                cursor: "pointer",
                color: "#4f46e5",
                fontWeight: "600",
              }}
            >
              {userId?.slice(0, 6)}...
            </span>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "email",
        header: "Email",
      },
      {
        accessorKey: "lastLogin",
        enableSorting: false,
        header: () => (
          <LoginDropdown
            onApply={(payload) => handlersRef.current.handleApplyFilter(payload)}
          />
        ),
        cell: ({ row }) => {
          const value = row.original.lastLogin;

          if (!value) {
            return <span style={{ color: "#999" }}>No Recent Login</span>;
          }

          const formatted = new Date(value).toLocaleString("en-IN", {
            dateStyle: "medium",
            timeStyle: "short",
          });

          return <span>{formatted}</span>;
        },
      },
      {
        id: "courses",
        header: "Courses & Types",
        cell: ({ row }) => {
          const courses = row.original.courseDetails || [];

          if (courses.length === 0) return "No course available";

          const visible = courses.slice(0, 2);
          const remaining = courses.length - 2;
          const allCourses = courses
            .map((c) => `${c.courseName} (${c.planType})`)
            .join(", ");

          return (
            <div className="course-cell">
              {visible.map((c, i) => (
                <span key={i} className="chip">
                  {c.courseName}
                  <span className={`badge ${(c.planType || "free").toLowerCase()}`}>
                    {c.planType}
                  </span>
                </span>
              ))}

              {remaining > 0 && (
                <span className="more" title={allCourses}>
                  +{remaining} more
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "assignSubscription",
        header: "Assign Subscription",
        cell: ({ row }) => (
          <div className="actions">
            <button
              type="button"
              className="add-subscription-btn"
              onClick={() => handlersRef.current.openSubscriptionPopup(row.original)}
            >
              Add Subscription
            </button>
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          // FIX: `!== null` treated undefined as active
          const isActive = Boolean(row.original.lastLogin);

          return (
            <span className={`status ${isActive ? "active" : "inactive"}`}>
              {isActive ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        id: "reports",
        header: "Review & Reports",
        cell: ({ row }) => (
          <div className="actions">
            <span
              style={{ cursor: "pointer" }}
              onClick={() => handlersRef.current.getreport(row.original.userId)}
            >
              📊
            </span>
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: Array.isArray(data) ? data : [],
    columns,

    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
    },

    onSortingChange: setSorting,

    manualPagination: true,
    manualFiltering: true,
    manualSorting: false,

    pageCount: totalPages,

    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="container" style={{ position: "relative" }}>
      {tableLoading && (
        <div className="table-loading-overlay">
          <div className="spinner" />
          <p>Loading...</p>
        </div>
      )}

      <input
        value={search}
        className="search"
        onChange={(e) => {
          setSearch(e.target.value);
          setPageIndex(0);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSearch();
        }}
        placeholder="Search user with name , email , UserId"
      />

      <button className="search-btn" onClick={handleSearch}>
        Search
      </button>

      <button title="Reset All Filters" className="reset" onClick={handleReset}>
        <RefreshCcw />
      </button>

      <button
        className="add-student-btn"
        onClick={() => setShowAddStudent(true)}
      >
        + Add Students
      </button>

      {showSubscriptionPopup && subscriptionTarget && (
        <form
          className="inline-subscription-bar"
          onSubmit={handleGrantSubscription}
        >
          <span
            className="inline-subscription-user"
            title={subscriptionTarget.userId}
          >
            {subscriptionTarget.userId}
          </span>

          <select
            name="courseName"
            value={subscriptionForm.courseName}
            onChange={handleSubscriptionChange}
            required
          >
            {subscriptionTarget.courses.map((c) => (
              <option key={c.courseName} value={c.courseName}>
                {c.courseName}
              </option>
            ))}
          </select>

          <select
            name="planType"
            value={subscriptionForm.planType}
            onChange={handleSubscriptionChange}
          >
            {PLAN_TYPES.map((plan) => (
              <option key={plan} value={plan}>
                {plan}
              </option>
            ))}
          </select>

          <input
            type="date"
            name="expiryDate"
            value={subscriptionForm.expiryDate}
            onChange={handleSubscriptionChange}
            title="Expiry date (optional)"
          />

          <button
            type="submit"
            className="inline-subscription-submit"
            disabled={subscriptionSubmitting}
          >
            {subscriptionSubmitting ? "Assigning..." : "Confirm"}
          </button>

          <button
            type="button"
            className="inline-subscription-cancel"
            onClick={closeSubscriptionPopup}
          >
            ✕
          </button>

          {subscriptionError && (
            <span className="inline-subscription-error">
              {subscriptionError}
            </span>
          )}
        </form>
      )}

      <div className="table-wrapper">
        <table className="table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}

                    {{
                      asc: " 🔼",
                      desc: " 🔽",
                    }[header.column.getIsSorted()] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{
                    textAlign: "center",
                    padding: "30px",
                    color: "#777",
                    fontSize: "16px",
                  }}
                >
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showPopup && (
        <div className="popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="popup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h2 className="report-heading">Test Reports</h2>
              <button
                className="popup-close"
                onClick={() => setShowPopup(false)}
              >
                ✕
              </button>
            </div>

            <div className="report-container">
              {reportsLoading ? (
                <p className="no-reports">Loading...</p>
              ) : reportsError ? (
                <p className="no-reports">{reportsError}</p>
              ) : Reports.length > 0 ? (
                Reports.map((item) => (
                  <div className="report-card" key={item}>
                    <p className="report-name">
                      <TestLabel name={item} />
                    </p>
                    <button
                      className="download-btn"
                      disabled={downloading === item}
                      onClick={() => Downloadapi(item, selectedUser)}
                    >
                      {downloading === item ? "Downloading..." : "Download"}
                    </button>
                  </div>
                ))
              ) : (
                <p className="no-reports">No Reports Available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddStudent && (
        <div className="popup-overlay" onClick={closeAddStudent}>
          <div className="popup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h2 className="report-heading">Add Student</h2>
              <button className="popup-close" onClick={closeAddStudent}>
                ✕
              </button>
            </div>

            <form className="student-form" onSubmit={handleAddStudent}>
              <input
                name="fullName"
                placeholder="Full Name"
                value={studentForm.fullName}
                onChange={handleStudentChange}
                required
              />
              <input
                name="lastName"
                placeholder="Last Name"
                value={studentForm.lastName}
                onChange={handleStudentChange}
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={studentForm.email}
                onChange={handleStudentChange}
                required
              />
              <input
                name="mobileNo"
                placeholder="Mobile No"
                value={studentForm.mobileNo}
                onChange={handleStudentChange}
              />
              <input
                name="collegeName"
                placeholder="College Name"
                value={studentForm.collegeName}
                onChange={handleStudentChange}
              />
              <input
                name="collegeID"
                placeholder="College ID"
                value={studentForm.collegeID}
                onChange={handleStudentChange}
              />

              <div className="restrict-access-box">
                <p className="restrict-label">
                  Strictly restrict access to specific IPs?
                </p>
                <div className="restrict-toggle">
                  <label>
                    <input
                      type="radio"
                      name="isStrictlyRestricted"
                      checked={studentForm.isStrictlyRestricted === true}
                      onChange={() => handleRestrictedChange(true)}
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="isStrictlyRestricted"
                      checked={studentForm.isStrictlyRestricted === false}
                      onChange={() => handleRestrictedChange(false)}
                    />
                    No
                  </label>
                </div>

                {studentForm.isStrictlyRestricted && (
                  <div className="ip-list">
                    {studentForm.allowedIps.map((ip, idx) => (
                      <div className="ip-row" key={idx}>
                        <input
                          type="text"
                          placeholder="e.g. 47.31.138.15"
                          value={ip}
                          onChange={(e) => handleIpChange(idx, e.target.value)}
                        />
                        {studentForm.allowedIps.length > 1 && (
                          <button
                            type="button"
                            className="ip-remove-btn"
                            onClick={() => removeIpField(idx)}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}

                    {/* FIX: addIpField existed but had no button, so only one IP could be entered */}
                    <button
                      type="button"
                      className="ip-add-btn"
                      onClick={addIpField}
                    >
                      + Add another IP
                    </button>
                  </div>
                )}
              </div>

              {studentError && <p className="form-error">{studentError}</p>}

              <button
                type="submit"
                className="filterapply"
                disabled={submitting}
              >
                {submitting ? "Adding..." : "Add Student"}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="pagination">
        <button
          disabled={pageIndex === 0}
          onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
        >
          Previous
        </button>

        {totalPages > 0 &&
          [...Array(end - start).keys()].map((i) => {
            const page = start + i;

            return (
              <button
                key={page}
                className={pageIndex === page ? "active" : ""}
                onClick={() => setPageIndex(page)}
              >
                {page + 1}
              </button>
            );
          })}

        <button
          disabled={pageIndex >= totalPages - 1}
          onClick={() =>
            setPageIndex((prev) => Math.min(prev + 1, totalPages - 1))
          }
        >
          Next
        </button>
      </div>
    </div>
  );
}